import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Clock, Plus, RefreshCw, Calendar, CheckCircle, XCircle, AlertCircle,
  User, Timer, Coffee, MoreHorizontal, Filter } from
'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400',
  submitted: 'bg-blue-500/20 text-blue-400',
  approved: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400'
};

export const TimeAttendance = () => {const { t } = useTranslation("hr");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: '', work_date: '', shift_id: '', clock_in: '', clock_out: '', break_minutes: 0, notes: ''
  });

  const fetchEntries = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/hr/time-entries?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch time entries');
      const data = await res.json();
      // API returns array directly
      setEntries(Array.isArray(data) ? data : data.entries || []);
    } catch (err) {
      toast.error(err.message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/employees?status=active`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.employees || []);
      }
    } catch (err) {console.error(err);}
  };

  const fetchShifts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/shifts`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        // API returns array directly
        setShifts(Array.isArray(data) ? data : data.shifts || []);
      }
    } catch (err) {console.error(err);}
  };

  useEffect(() => {fetchEntries();fetchEmployees();fetchShifts();}, [statusFilter]);

  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        employee_id: parseInt(formData.employee_id),
        shift_id: formData.shift_id ? parseInt(formData.shift_id) : null,
        break_minutes: parseInt(formData.break_minutes) || 0
      };
      const res = await fetch(`${API_URL}/api/hr/time-entries`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to create time entry');
      toast.success('Time entry created');
      setShowCreate(false);
      setFormData({ employee_id: '', work_date: '', shift_id: '', clock_in: '', clock_out: '', break_minutes: 0, notes: '' });
      fetchEntries();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSubmit = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/time-entries/${id}/submit`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to submit');
      toast.success('Time entry submitted for approval');
      fetchEntries();
    } catch (err) {toast.error(err.message);}
  };

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/time-entries/${id}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to approve');
      toast.success('Time entry approved');
      fetchEntries();
    } catch (err) {toast.error(err.message);}
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      const res = await fetch(`${API_URL}/api/hr/time-entries/${id}/reject?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to reject');
      toast.success('Time entry rejected');
      fetchEntries();
    } catch (err) {toast.error(err.message);}
  };

  const pendingCount = entries.filter((e) => e.status === 'submitted').length;
  const approvedCount = entries.filter((e) => e.status === 'approved').length;
  const totalHours = entries.reduce((sum, e) => sum + (e.total_hours || 0), 0);

  return (
    <div className="space-y-6" data-testid="time-attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("timeAttendance", "Time & Attendance")}</h1>
          <p className="text-muted-foreground mt-1">{t("trackEmployeeWork", "Track employee work hours and attendance")}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4" />{t("addTimeEntry", "Add Time Entry")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalEntries", "Total Entries")}</p>
                <p className="text-3xl font-bold">{entries.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20"><Clock className="h-6 w-6 text-blue-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("pendingApproval", "Pending Approval")}</p>
                <p className="text-3xl font-bold">{pendingCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/20"><AlertCircle className="h-6 w-6 text-amber-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("approved", "Approved")}</p>
                <p className="text-3xl font-bold">{approvedCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20"><CheckCircle className="h-6 w-6 text-green-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalHours", "Total Hours")}</p>
                <p className="text-3xl font-bold">{totalHours.toFixed(1)}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/20"><Timer className="h-6 w-6 text-purple-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t("timeEntries", "Time Entries")}</CardTitle>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                  <SelectItem value="draft">{t("draft", "Draft")}</SelectItem>
                  <SelectItem value="submitted">{t("submitted", "Submitted")}</SelectItem>
                  <SelectItem value="approved">{t("approved", "Approved")}</SelectItem>
                  <SelectItem value="rejected">{t("rejected", "Rejected")}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchEntries}><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ?
          <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div> :
          entries.length === 0 ?
          <div className="text-center py-12">
              <Clock className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">{t("noTimeEntries", "No time entries found")}</p>
              <Button onClick={() => setShowCreate(true)} className="mt-4 bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" />{t("addFirstEntry", "Add First Entry")}
            </Button>
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">{t("employee", "Employee")}</th>
                    <th className="text-left p-3 font-medium">{t("date", "Date")}</th>
                    <th className="text-left p-3 font-medium">{t("clockIn", "Clock In")}</th>
                    <th className="text-left p-3 font-medium">{t("clockOut", "Clock Out")}</th>
                    <th className="text-left p-3 font-medium">{t("break", "Break")}</th>
                    <th className="text-left p-3 font-medium">{t("totalHours", "Total Hours")}</th>
                    <th className="text-left p-3 font-medium">{t("status", "Status")}</th>
                    <th className="text-right p-3 font-medium">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {entries.map((entry) =>
                <tr key={entry.id} className="hover:bg-slate-800/30">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {entry.employee_name || `Employee #${entry.employee_id}`}
                        </div>
                      </td>
                      <td className="p-3">{entry.work_date}</td>
                      <td className="p-3">{entry.clock_in ? new Date(entry.clock_in).toLocaleTimeString() : '-'}</td>
                      <td className="p-3">{entry.clock_out ? new Date(entry.clock_out).toLocaleTimeString() : '-'}</td>
                      <td className="p-3">{entry.break_minutes || 0}{t("min", "min")}</td>
                      <td className="p-3 font-medium">{entry.total_hours?.toFixed(2) || 0}{t("hrs", "hrs")}</td>
                      <td className="p-3">
                        <Badge className={statusColors[entry.status]}>{entry.status}</Badge>
                      </td>
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {entry.status === 'draft' &&
                        <DropdownMenuItem onClick={() => handleSubmit(entry.id)}>
                                <CheckCircle className="h-4 w-4 mr-2" />{t("submitForApproval", "Submit for Approval")}
                        </DropdownMenuItem>
                        }
                            {entry.status === 'submitted' &&
                        <>
                                <DropdownMenuItem onClick={() => handleApprove(entry.id)}>
                                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />{t("approve", "Approve")}
                          </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleReject(entry.id)} className="text-red-400">
                                  <XCircle className="h-4 w-4 mr-2" />{t("reject", "Reject")}
                          </DropdownMenuItem>
                              </>
                        }
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("addTimeEntry", "Add Time Entry")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>{t("employee", "Employee *")}</Label>
              <Select value={formData.employee_id} onValueChange={(v) => setFormData({ ...formData, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((emp) =>
                  <SelectItem key={emp.id} value={String(emp.id)}>
                      {emp.full_name || `${emp.first_name} ${emp.last_name}`}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("workDate", "Work Date *")}</Label>
                <Input type="date" value={formData.work_date} onChange={(e) => setFormData({ ...formData, work_date: e.target.value })} />
              </div>
              <div>
                <Label>{t("shift", "Shift")}</Label>
                <Select value={formData.shift_id || 'none'} onValueChange={(v) => setFormData({ ...formData, shift_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Select shift" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("none", "None")}</SelectItem>
                    {shifts.map((s) =>
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("clockIn", "Clock In")}</Label>
                <Input type="datetime-local" value={formData.clock_in} onChange={(e) => setFormData({ ...formData, clock_in: e.target.value })} />
              </div>
              <div>
                <Label>{t("clockOut", "Clock Out")}</Label>
                <Input type="datetime-local" value={formData.clock_out} onChange={(e) => setFormData({ ...formData, clock_out: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("breakMinutes", "Break (minutes)")}</Label>
                <Input type="number" value={formData.break_minutes} onChange={(e) => setFormData({ ...formData, break_minutes: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>{t("notes", "Notes")}</Label>
              <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">{t("createEntry", "Create Entry")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default TimeAttendance;
